import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import type { Sensor } from '@/types';
import { 
  Radio, 
  Thermometer, 
  Droplets, 
  Battery, 
  MapPin,
  Wifi,
  WifiOff,
  Calendar,
  Zap,
  Activity
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface SensorDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sensor: Sensor | null;
  // CAMBIO v0.2.0: empresa en lugar de jardín
  companyName?: string;
}

export function SensorDetailDialog({
  open,
  onOpenChange,
  sensor,
  companyName,
}: SensorDetailDialogProps) {
  if (!sensor) return null;
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'border-success/50 text-success';
      case 'offline': return 'border-destructive/50 text-destructive';
      case 'warning': return 'border-warning/50 text-warning';
      default: return '';
    }
  };

  const getBatteryColor = (battery?: number) => {
    if (!battery) return 'text-muted-foreground';
    if (battery > 50) return 'text-success';
    if (battery > 20) return 'text-warning';
    return 'text-destructive';
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Radio className="w-5 h-5 text-info" />
            {sensor.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status and badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={cn('gap-1.5', getStatusColor(sensor.status))}>
              {sensor.status === 'online' ? (
                <Wifi className="w-3 h-3" />
              ) : (
                <WifiOff className="w-3 h-3" />
              )}
              {sensor.status === 'online' ? 'Conectado' : sensor.status === 'offline' ? 'Desconectado' : 'Advertencia'}
            </Badge>
            <Badge variant="secondary">{sensor.model}</Badge>
            <Badge variant="outline">{sensor.communicationType.toUpperCase()}</Badge>
          </div>

          {/* Last reading */}
          {sensor.lastReading && (
            <div className="p-4 rounded-lg bg-muted/50 space-y-4">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <Activity className="w-4 h-4 text-primary" />
                Última Lectura
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                    <Thermometer className="w-4 h-4" />
                  </div>
                  <p className="text-2xl font-display font-bold">{sensor.lastReading.temperature}°C</p>
                  <p className="text-xs text-muted-foreground">Temperatura</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                    <Droplets className="w-4 h-4" />
                  </div>
                  <p className="text-2xl font-display font-bold text-info">{sensor.lastReading.humidity}%</p>
                  <p className="text-xs text-muted-foreground">Humedad</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                    <Zap className="w-4 h-4" />
                  </div>
                  <p className="text-2xl font-display font-bold">{sensor.lastReading.conductivity || '-'}</p>
                  <p className="text-xs text-muted-foreground">µS/cm</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                {formatDate(sensor.lastReading.timestamp)}
              </p>
            </div>
          )}

          {/* Device info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Información del Dispositivo</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">DevEUI</span>
                  <span className="font-mono text-xs">{sensor.deviceEUI}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Modelo</span>
                  <span>{sensor.model}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Batería</span>
                  <span className={cn('flex items-center gap-1', getBatteryColor(sensor.battery))}>
                    <Battery className="w-4 h-4" />
                    {sensor.battery}%
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-sm">Ubicación</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Jardín</span>
                  <span>{companyName || 'Sin asignar'}</span>
                </div>
                {sensor.coordinates && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Coordenadas</span>
                    <span className="font-mono text-xs">
                      {sensor.coordinates.lat.toFixed(4)}, {sensor.coordinates.lng.toFixed(4)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="pt-4 border-t border-border">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground text-xs">Creado</p>
                  <p>{formatDate(sensor.createdAt)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground text-xs">Última actividad</p>
                  <p>{sensor.lastSeen ? formatDate(sensor.lastSeen) : 'Nunca'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
